/**
 * Sunset phone wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wallpapers_iv`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const sunsetPhoneWallpapersPack: ToilePack = {
    slug: 'sunset-phone-wallpapers',
    category: 'screens',
    name: 'Sunset phone wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'palms', label: 'Palm silhouettes', subject: 'black silhouettes of palm trees at the bottom of the screen against a pink, orange and violet sky'},
        {key: 'clouds', label: 'Cloud sunset', subject: 'a sun setting over the sea beneath glowing orange and purple clouds, rays breaking through'},
        {key: 'field', label: 'Golden field', subject: 'a field of tall grass glowing gold at the bottom of the screen, the sun just touching the horizon'},
    ],
    styles: [
        {key: 'glow', label: 'Glow gradient', style: 'soft airbrushed sky gradient, smooth blended light, dreamy haze, clean silhouettes', palette: 'pink, coral, deep orange and violet', framing: 'Full-bleed, the silhouettes at the bottom and the sky calm above'},
        {key: 'photo', label: 'Sky photo', style: 'real sunset photograph look, rays through clouds, sharp horizon, natural color', palette: 'deep orange, gold and magenta', framing: 'Full-bleed, the horizon low in the lower third'},
        {key: 'retro', label: 'Retro sun', style: 'retro synth poster look, flat banded sun, clean shapes, subtle grain', palette: 'hot pink, orange and purple', framing: 'Full-bleed, the sun low with the sky calm above'},
    ],
    directions: ['purple sky', 'desert', 'city skyline'],
    directionHint: 'try: purple sky',
    seo: {
        title: 'Sunset Phone Wallpapers: Palms, Clouds, Golden Field',
        metaDescription: 'Sunset phone wallpapers from pink to deep orange: palm silhouettes, a sun over the sea and a golden field, each in three styles.',
        h1: 'Sunset wallpapers: palm silhouettes, a cloud sunset, a golden field',
        tagline: 'Nine warm phone wallpapers, three sunsets in three styles.',
        intro: 'Phone wallpapers of sunsets: black palm silhouettes against a pink and violet sky, a sun setting over the sea through glowing clouds, and a golden field with the sun on the horizon. Each comes as a soft airbrushed sky, a real sky photo look and a retro synth sun. People who want a warm, glowing screen can set them as they are. Type a different scene, such as a desert, and the styles carry it.',
        useCases: [
            'a phone lock screen',
            'a warm tablet or desktop background',
            'a beach house or bedroom print',
            'a gift for someone who loves golden hour',
        ],
        faq: [
            {q: 'What are the nine pictures?', a: 'Three sunset scenes: palm silhouettes, a sun over the sea through clouds, and a golden field at the horizon. Each scene is shown in three styles.'},
            {q: 'Can I change the scene?', a: 'Yes. Type a scene such as a purple sky, a desert or a city skyline; a typed scene is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Glow gradient is a soft, airbrushed sky with blended light. Sky photo has a real sunset look with rays through clouds. Retro sun is a flat banded sun in a synth poster style.'},
            {q: 'Are these photographs?', a: 'No. The sky photo style has a photograph look, but all nine pictures are rendered images.'},
        ],
    },
}
